import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Wallet() {
  const { refresh } = useAuth();
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("500");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [w, t] = await Promise.all([
      api.get("/wallet/me"),
      api.get("/wallet/transactions"),
    ]);
    setBalance(w.data.balance);
    setTxns(t.data);
  };
  useEffect(() => {
    load();
  }, []);

  const topup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/topup", { amount: +amount });
      setBalance(data.balance);
      toast.success(`Added ${formatINR(amount)} to wallet`);
      await refresh();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="wallet-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">Wallet</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          Your digital balance.
        </h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 rounded-3xl border border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c] p-8 ml-glow">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E8D6A3] mb-3">
            Balance
          </div>
          <div className="text-5xl font-black" data-testid="wallet-balance">
            {formatINR(balance)}
          </div>
          <form onSubmit={topup} className="mt-8 flex gap-2">
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              min={1}
              data-testid="wallet-amount-input"
              className="bg-black/40 border-white/10 flex-1"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="wallet-topup-btn"
              className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-6"
            >
              Top up
            </Button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            {[200, 500, 1000, 2000].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs text-zinc-300 hover:text-white"
              >
                {formatINR(v)}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 rounded-3xl border border-white/10 bg-white/[0.02] p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
            Recent transactions
          </div>
          {txns.length === 0 && (
            <div className="text-zinc-500 text-sm">No transactions yet.</div>
          )}
          <div className="divide-y divide-white/5">
            {txns.map((t) => (
              <div key={t.id} className="py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold">{t.description}</div>
                  <div className="text-xs text-zinc-500">
                    {new Date(t.timestamp).toLocaleString()}
                  </div>
                </div>
                <div
                  className={`font-black ${
                    t.type === "credit" ? "text-emerald-400" : "text-zinc-200"
                  }`}
                >
                  {t.type === "credit" ? "+" : ""}
                  {formatINR(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
